import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SportsHockeyIcon from "@mui/icons-material/SportsHockey";
import { alpha } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { fireConfetti } from "../../utils/fireConfetti";
import { PLAN_DEFINITIONS, getParticipantLimitLabel } from "../../config/plans";
import { activateFreePlan, createCheckoutSession, fetchMySubscription, devSwitchPlan } from "../../services/api/subscriptions";
import AuthPromptDialog from "../../components/AuthPromptDialog";
import ProfileCompleteDialog, { isProfileComplete } from "../../components/ProfileCompleteDialog";
import LoginDialog from "../auth/login-dialog";
import { hasLocallyValidSession } from "../../services/auth/auth-session";
import { fetchMyProfile } from "../../services/auth/profile-api";
import { pricingSubscriptionsEnabled, canUserSubscribe } from "../../config/pricing-access";
import "./styles.css";

const BYPASS_STRIPE_CHECKOUT = process.env.REACT_APP_BYPASS_STRIPE === "true";

const PLAN_ACCENTS = {
  launch: "#4caf50",
  "pickup-pro": "#1976d2",
  league: "#7c3aed",
};

export default function PricingView() {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = React.useState("monthly");
  const [checkoutError, setCheckoutError] = React.useState("");
  const [authPromptPlan, setAuthPromptPlan] = React.useState(null);
  const [loginOpen, setLoginOpen] = React.useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = React.useState(false);
  const [profileData, setProfileData] = React.useState(null);
  const [pendingPlanId, setPendingPlanId] = React.useState(null);
  const [welcomeOpen, setWelcomeOpen] = React.useState(false);
  const [welcomePlanId, setWelcomePlanId] = React.useState(null);
  const [welcomePrevPlanId, setWelcomePrevPlanId] = React.useState(null);
  const [currentPlanId, setCurrentPlanId] = React.useState(null);
  const [userEmail, setUserEmail] = React.useState(null);

  React.useEffect(() => {
    if (!hasLocallyValidSession()) return;
    fetchMySubscription().then((sub) => {
      if (sub?.planId) setCurrentPlanId(sub.planId);
    }).catch(() => {});
    fetchMyProfile().then((profile) => {
      if (profile?.email) setUserEmail(profile.email);
    }).catch(() => {});
  }, []);

  const subscribeAllowed = canUserSubscribe(userEmail);


  const doCheckout = React.useCallback(async (planId) => {
    try {
      setCheckoutError("");
      const checkout = await createCheckoutSession({ planId, billingCycle });
      if (checkout.url) {
        window.location.assign(checkout.url);
      }
    } catch (error) {
      setCheckoutError(error instanceof Error ? error.message : "Unable to create checkout session");
    }
  }, [billingCycle]);

  const handleCheckout = async (planId) => {
    if (!hasLocallyValidSession()) {
      const plan = PLAN_DEFINITIONS.find((p) => p.id === planId);
      setAuthPromptPlan(plan);
      return;
    }

    if (planId === "launch") {
      try {
        await activateFreePlan();
        setWelcomePrevPlanId(currentPlanId);
        setWelcomePlanId("launch");
        setCurrentPlanId("launch");
        setWelcomeOpen(true);
        fireConfetti();
      } catch (error) {
        setCheckoutError(error instanceof Error ? error.message : "Unable to activate free plan");
      }
      return;
    }

    if (BYPASS_STRIPE_CHECKOUT) {
      try {
        await devSwitchPlan({ planId, billingCycle });
        const prevIdx = PLAN_DEFINITIONS.findIndex((p) => p.id === currentPlanId);
        const nextIdx = PLAN_DEFINITIONS.findIndex((p) => p.id === planId);
        setWelcomePrevPlanId(currentPlanId);
        setWelcomePlanId(planId);
        setCurrentPlanId(planId);
        setWelcomeOpen(true);
        if (nextIdx > prevIdx) fireConfetti();
      } catch (error) {
        setCheckoutError(error instanceof Error ? error.message : "Unable to switch plan");
      }
      return;
    }

    try {
      const profile = await fetchMyProfile();
      if (!isProfileComplete(profile)) {
        setProfileData(profile);
        setPendingPlanId(planId);
        setProfileDialogOpen(true);
        return;
      }
      await doCheckout(planId);
    } catch (error) {
      setCheckoutError(error instanceof Error ? error.message : "Unable to start checkout");
    }
  };

  const handleProfileCompleteClose = ({ updated }) => {
    setProfileDialogOpen(false);
    if (updated && pendingPlanId) {
      doCheckout(pendingPlanId);
    }
    setPendingPlanId(null);
    setProfileData(null);
  };

  return (
    <Container maxWidth="lg" className="pricing-page">
      <Box
        className="pricing-header-bg"
        sx={{
          background: (theme) =>
            theme.palette.mode === "dark"
              ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.18)} 0%, ${alpha(theme.palette.primary.main, 0.08)} 60%, transparent 100%)`
              : "linear-gradient(135deg, #e3f0ff 0%, #f0f4ff 60%, #ffffff 100%)",
        }}
      >
        <Stack spacing={2} className="pricing-header">
          <Typography variant="h3" component="h3">
            Puck-Pickup Pricing
          </Typography>
          <Typography variant="h6" color="text.secondary">
            No platform fees. No minimums. Built for hockey.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            One subscription price. Payment processing (e.g. Stripe) is separate and passed through at
            standard processor rates—we don&apos;t add a percentage on top. Clear participant limits
            so you know when to upgrade.
          </Typography>
          <Box style={{ marginTop: "1.5rem" }}>
            <ToggleButtonGroup
              value={billingCycle}
              exclusive
              onChange={(_, val) => { if (val) setBillingCycle(val); }}
              className="billing-toggle"
              sx={{ gap: 1 }}
            >
              <ToggleButton value="monthly">Monthly</ToggleButton>
              <ToggleButton value="annual">
                Annual
                <Chip label="Save" size="small" color="success" sx={{ ml: 1, height: 18, fontSize: "0.65rem" }} />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {checkoutError ? <Alert severity="error">{checkoutError}</Alert> : null}
          {hasLocallyValidSession() && !subscribeAllowed && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Your account is not authorized to subscribe at this time. Contact us if you believe this is a mistake.
            </Alert>
          )}
        </Stack>
      </Box>

      <Grid container spacing={3}>
        {PLAN_DEFINITIONS.map((plan) => {
          const isPopular = plan.id === "pickup-pro";
          const isCurrent = plan.id === currentPlanId;
          const accentColor = PLAN_ACCENTS[plan.id];
          return (
            <Grid item xs={12} md={6} lg={4} key={plan.id}>
              <Box sx={{ position: "relative", height: "100%" }}>
                {isCurrent && (
                  <Chip
                    label="Your Plan"
                    size="small"
                    className="popular-badge"
                    sx={{ bgcolor: accentColor, color: "#fff" }}
                  />
                )}
                {!isCurrent && isPopular && (
                  <Chip
                    label="Most Popular"
                    color="primary"
                    size="small"
                    className="popular-badge"
                  />
                )}
                <Card
                  className={`pricing-card${isPopular ? " pricing-card-popular" : ""}`}
                  variant="outlined"
                  sx={{
                    borderTop: `4px solid ${accentColor}`,
                    height: "100%",
                    ...(isCurrent && {
                      borderColor: accentColor,
                      boxShadow: (theme) => `0 0 0 2px ${accentColor}${theme.palette.mode === "dark" ? "99" : "55"}`,
                    }),
                  }}
                >
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Typography variant="h5">{plan.name}</Typography>
                      {plan.positioning && (
                        <Typography variant="body2" color="text.secondary">
                          {plan.positioning}
                        </Typography>
                      )}
                      {plan.freeTierBlurb && (
                        <Typography variant="body2" color="text.secondary">
                          {plan.freeTierBlurb}
                        </Typography>
                      )}
                      <Chip
                        size="small"
                        color="primary"
                        variant="outlined"
                        label={getParticipantLimitLabel(plan.participantLimit)}
                      />
                      <Box sx={{ py: 0.5 }}>
                        {plan.monthlyPrice === 0 ? (
                          <>
                            <Typography variant="h4">Free</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Always free · No credit card required
                            </Typography>
                          </>
                        ) : billingCycle === "annual" ? (
                          <>
                            <Typography variant="h4">{plan.annualPriceLabel}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              billed annually
                              {plan.annualPriceYear > 0 ? ` ($${plan.annualPriceYear}/year)` : ""}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              or ${plan.monthlyPrice}/month month-to-month
                            </Typography>
                          </>
                        ) : (
                          <>
                            <Typography variant="h4">${plan.monthlyPrice}/month</Typography>
                            <Typography variant="body2" color="text.secondary">
                              month-to-month
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              or {plan.annualPriceLabel} when billed annually
                              {plan.annualPriceYear > 0 ? ` ($${plan.annualPriceYear}/year)` : ""}
                            </Typography>
                          </>
                        )}
                      </Box>
                      <Stack className="feature-list">
                        {plan.features.map((feature) => (
                          <Stack key={feature} direction="row" spacing={1} alignItems="flex-start">
                            <CheckCircleOutlineIcon
                              sx={{ fontSize: 16, color: accentColor, mt: "2px", flexShrink: 0 }}
                            />
                            <Typography variant="body2">{feature}</Typography>
                          </Stack>
                        ))}
                      </Stack>
                      {!pricingSubscriptionsEnabled || (!subscribeAllowed && !isCurrent) ? (
                        <Button variant={plan.id === "launch" ? "outlined" : "contained"} disabled fullWidth>
                          Coming Soon!
                        </Button>
                      ) : (
                        <Button
                          variant={plan.id === "launch" ? "outlined" : "contained"}
                          onClick={() => handleCheckout(plan.id)}
                          disabled={isCurrent}
                          fullWidth
                        >
                          {isCurrent ? "Current plan" : plan.cta}
                        </Button>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      <AuthPromptDialog
        open={Boolean(authPromptPlan)}
        onClose={() => setAuthPromptPlan(null)}
        onConfirm={() => { setAuthPromptPlan(null); setLoginOpen(true); }}
        title="Sign in to get started"
        body={
          <>
            You&apos;ll need a free Puck-Pickup account to subscribe to{" "}
            <strong>{authPromptPlan?.name ?? "this plan"}</strong>. It only takes a minute!
          </>
        }
      />

      <LoginDialog
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        onSuccess={() => setLoginOpen(false)}
      />

      <ProfileCompleteDialog
        open={profileDialogOpen}
        onClose={handleProfileCompleteClose}
        profile={profileData}
      />

      <Dialog open={welcomeOpen} maxWidth="xs" fullWidth>
        <DialogContent>
          {(() => {
            const welcomePlan = PLAN_DEFINITIONS.find((p) => p.id === welcomePlanId);
            const isLaunch = welcomePlanId === "launch";
            const prevIdx = PLAN_DEFINITIONS.findIndex((p) => p.id === welcomePrevPlanId);
            const nextIdx = PLAN_DEFINITIONS.findIndex((p) => p.id === welcomePlanId);
            const isUpgrade = nextIdx > prevIdx;
            const isDowngrade = nextIdx < prevIdx;
            return (
              <Stack spacing={3} alignItems="center" sx={{ py: 2, textAlign: "center" }}>
                <SportsHockeyIcon sx={{ fontSize: 56, color: "#C8102E" }} />
                <Typography variant="h5" fontWeight={700}>
                  {isLaunch ? "You're in!" : isUpgrade ? "You're upgraded!" : "Plan switched"}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: -1.5 }}>
                  {isLaunch
                    ? <>Welcome to the <strong>Launch</strong> plan — free, forever.</>
                    : <>You&apos;re now on the <strong>{welcomePlan?.name ?? "new"}</strong> plan.</>}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {isLaunch
                    ? "Lace 'em up — your Puck-Pickup league is ready to drop the puck. Set up your first league and get your crew on the schedule."
                    : isDowngrade
                    ? "Your plan has been updated. Some features from your previous plan may no longer be available."
                    : "Your new plan is active. Head to your leagues to start taking advantage of your upgraded limits."}
                </Typography>
                {isLaunch && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                    No penalties for beginners. We promise.
                  </Typography>
                )}
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={() => { setWelcomeOpen(false); navigate(isLaunch ? "/new-league" : "/my-leagues"); }}
                  sx={{ mt: 1, fontWeight: 700, fontSize: "1.05rem" }}
                >
                  Got it
                </Button>
              </Stack>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Box sx={{ mt: 4, mb: 2 }}>
        <Divider sx={{ mb: 2 }} />
        <Stack spacing={1}>
          <Typography variant="subtitle2" color="text.secondary">
            What&apos;s not included
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Payment processing (e.g. Stripe) is separate and passed through at standard processor
            rates. We don&apos;t add a platform or technology fee on top.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No minimum processing requirement. No technology fee. No surprise renewal fees.
          </Typography>
        </Stack>
      </Box>
    </Container>
  );
}
